import React from 'react';

import { Theme } from '@lumapps/lumx/react';
import { useWindowResize } from '@lumapps/utils/hooks/useWindowResize';
import { Editor, ReactEditor, useSlate, useSlateSelection } from '@lumapps/wrex-core/slate';

import { useRefineContentDialog } from '../../hooks/useRefineContentDialog';
import { ButtonPosition, PopoverStyle } from '../../types';
import { getCurrentRange } from '../../utils/getCurrentRange';
import { getFloatingButtonPosition } from '../../utils/getFloatingButtonPosition';
import { getPopoverStyle } from '../../utils/getPopoverStyle';
import { UpdateContentDialog } from './UpdateContentDialog';
import { UpdateContentMenu } from './UpdateContentMenu';

import '../index.scss';

export interface UpdateContentButtonProps {
    theme: Theme;
}

export const UpdateContentButton = ({ theme }: UpdateContentButtonProps) => {
    const buttonRef = React.useRef(null);

    const editor = useSlate() as ReactEditor;

    const [currentRange, setCurrentRange] = React.useState<Range | undefined>(undefined);

    const slateSelection = useSlateSelection();
    const domSelection = window.getSelection();

    React.useEffect(() => {
        setCurrentRange(getCurrentRange(editor, slateSelection, domSelection));
    }, [slateSelection, domSelection, editor]);

    const selectedText = slateSelection ? Editor.string(editor, slateSelection) : undefined;

    const {
        newSuggestions,
        isRefiningContent,
        isPopoverOpen,
        onRefineContent,
        onInsertNewContent,
        onCancelRefinement,
        setNewSuggestions,
        setIsPopoverOpen,
    } = useRefineContentDialog();

    const onClose = React.useCallback(() => setIsPopoverOpen(false), [setIsPopoverOpen]);

    // Get button and popover positions and handle selection change
    const [buttonPosition, setButtonPosition] = React.useState<ButtonPosition | undefined>(
        getFloatingButtonPosition(isPopoverOpen, currentRange),
    );

    const [popoverStyle, setPopoverStyle] = React.useState<PopoverStyle | undefined>(
        getPopoverStyle(isPopoverOpen, currentRange),
    );

    const computePositions = React.useCallback(() => {
        if (currentRange) {
            setButtonPosition(getFloatingButtonPosition(isPopoverOpen, currentRange));
            setPopoverStyle(getPopoverStyle(isPopoverOpen, currentRange));
        }
    }, [currentRange, isPopoverOpen]);

    React.useEffect(() => {
        computePositions();
    }, [computePositions]);

    // Handle window resize for button and popover positions
    useWindowResize(() => computePositions());

    // If a new text is generated and the user clicks away, force the popover to close and delete generated texts
    React.useEffect(() => {
        setNewSuggestions([]);
        onClose();
    }, [currentRange, setNewSuggestions, onClose]);

    return currentRange ? (
        <>
            <UpdateContentMenu
                buttonRef={buttonRef}
                theme={theme}
                buttonPosition={buttonPosition}
                selectedText={selectedText}
                onRefineContent={onRefineContent}
            />

            {isPopoverOpen && (
                <UpdateContentDialog
                    isRefiningContent={isRefiningContent}
                    hasCurrentRange={Boolean(currentRange)}
                    anchorRef={buttonRef}
                    newSuggestions={newSuggestions}
                    popoverStyle={popoverStyle}
                    slateSelection={slateSelection}
                    onClose={onClose}
                    onCancelRefinement={onCancelRefinement}
                    onInsertNewContent={onInsertNewContent}
                    onRefineContent={onRefineContent}
                    setNewSuggestions={setNewSuggestions}
                />
            )}
        </>
    ) : null;
};
